// src/components/NotificationsIcon.js
import React, { useEffect, useRef, useState } from "react";
import { Sparkle } from "phosphor-react";
import { api } from "../api";
import { useNavigate, useLocation } from "react-router-dom";

const BLUE_600 = "#2563EB";
const BLUE_300 = "#93C5FD";
const BLUE_100 = "#DBEAFE";
const BLUE_700 = "#1D4ED8";

const LS_OUTGOING = "tp_outgoing_requests_v1";
const LS_SEEN = "tp_seen_notifications_v1";

const CONTENT_KINDS = new Set(["mention", "comment", "highfive", "comment_star"]);
const CONNECTION_KINDS = new Set(["incoming", "outgoing", "incoming_accepted"]);
const ALLOWED_KINDS = new Set([...CONTENT_KINDS, ...CONNECTION_KINDS]);

const TYPE_MAP = {
  connection_request: "incoming",
  connection_accepted: "incoming_accepted",
  connection_accept: "incoming_accepted",
  connection_received: "incoming",
  mention: "mention",
  tagged: "mention",
  activity_mention: "mention",
  comment_mention: "mention",
  activity_commented: "comment",
  comment: "comment",
  activity_comment: "comment",
  activity_high_fived: "highfive",
  high_five: "highfive",
  highfive: "highfive",
  activity_highfive: "highfive",
  comment_starred: "comment_star",
  comment_liked: "comment_star",
  comment_star: "comment_star",
};

const mapType = (t) => TYPE_MAP[String(t || "").toLowerCase()] || "other";
const sortByTimeThenName = (arr) =>
  [...arr].sort((a, b) => {
    const aT = Date.parse(a.meta?.created_at || "") || 0;
    const bT = Date.parse(b.meta?.created_at || "") || 0;
    if (aT !== bT) return bT - aT;
    return String(a.name).localeCompare(String(b.name));
  });

// ------- mobile helpers -------
function useIsMobile(breakpointPx = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpointPx : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpointPx);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpointPx]);
  return isMobile;
}
const isIOS = () =>
  typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent || "");

// ------- URL param helpers for popup state -------
const NOTIF_PARAM = "notifications";
const NOTIF_VALUE = "1";

function hasNotifParam(location) {
  const sp = new URLSearchParams(location.search);
  return sp.get(NOTIF_PARAM) === NOTIF_VALUE;
}
function addNotifParam(location, navigate) {
  const sp = new URLSearchParams(location.search);
  sp.set(NOTIF_PARAM, NOTIF_VALUE);
  navigate(`${location.pathname}?${sp.toString()}${location.hash || ""}`, { replace: true });
}
function removeNotifParam(location, navigate) {
  const sp = new URLSearchParams(location.search);
  sp.delete(NOTIF_PARAM);
  const next = sp.toString();
  navigate(`${location.pathname}${next ? `?${next}` : ""}${location.hash || ""}`, { replace: true });
}

export default function NotificationsIcon({ token: tokenProp, className = "" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [meId, setMeId] = useState(null);
  const [seenKeys, setSeenKeys] = useState(new Set());
  const ref = useRef(null);
  const isMobile = useIsMobile(640);
  const navigate = useNavigate();
  const location = useLocation();

  const getToken = () =>
    tokenProp ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    null;

  const authHeaders = () => {
    const t = getToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const normalizeName = (u) =>
    u?.display_name ||
    u?.name ||
    [u?.first_name, u?.last_name].filter(Boolean).join(" ") ||
    u?.username ||
    (u?.id ? `User ${u.id}` : "User");

  const userScopedKey = (base) => (meId ? `${base}:${meId}` : null);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const res = await api.get("/users/me", {
          headers: authHeaders(),
          withCredentials: true,
        });
        setMeId(res?.data?.id ?? null);
      } catch {
        setMeId(null);
      }
    };
    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenProp]);

  const loadSeen = () => {
    const key = userScopedKey(LS_SEEN);
    if (!key) return new Set();
    try {
      const raw = localStorage.getItem(key);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  };
  const saveSeen = (setObj) => {
    const key = userScopedKey(LS_SEEN);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(Array.from(setObj)));
    } catch {}
  };
  useEffect(() => {
    setSeenKeys(loadSeen());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meId]);

  const loadOutgoing = () => {
    const key = userScopedKey(LS_OUTGOING);
    if (!key) return [];
    try {
      const raw = localStorage.getItem(key);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };
  const saveOutgoing = (arr) => {
    const key = userScopedKey(LS_OUTGOING);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(arr));
    } catch {}
  };

  const safeIso = (val) => {
    if (!val) return "";
    try {
      if (typeof val === "string") return new Date(val).toISOString();
      return new Date(val).toISOString();
    } catch {
      return "";
    }
  };

  const fetchConnStatus = async (userId) => {
    if (!userId) return null;
    try {
      const res = await api.get(`/connections/status/${userId}`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      return res?.data?.status || null;
    } catch {
      return null;
    }
  };

  const fetchIncomingRequests = async () => {
    try {
      const res = await api.get("/connections/requests", {
        headers: authHeaders(),
        withCredentials: true,
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      const base = rows.map((r) => ({
        key: `in-${r.connection_id}`,
        id: r.connection_id,
        userId: r.requester?.id ?? null,
        name: normalizeName(r.requester),
        avatar: r.requester?.avatar_url || r.requester?.profile_pic_url || null,
        kind: "incoming",
        state: "request",
        followStatus: "idle",
        href: r.requester?.id ? `/profile/${r.requester.id}` : undefined,
        meta: { created_at: safeIso(r.created_at) },
      }));

      const hydrated = await Promise.all(
        base.map(async (it) => {
          const st = await fetchConnStatus(it.userId);
          if (st === "accepted") return { ...it, followStatus: "connected" };
          return it;
        })
      );
      return hydrated;
    } catch {
      return [];
    }
  };

  const refreshOutgoingStatuses = async (outgoing) => {
    const checks = await Promise.all(
      outgoing.map(async (o) => {
        if (o.state !== "sent" || !o.userId) return o;
        try {
          const res = await api.get(`/connections/status/${o.userId}`, {
            headers: authHeaders(),
            withCredentials: true,
          });
          const status = res?.data?.status;
          if (status === "accepted") return { ...o, state: "accepted" };
        } catch {}
        return o;
      })
    );
    saveOutgoing(checks.map(({ key, ...rest }) => rest));
    return checks;
  };

  const messageFor = (kind, name) => {
    switch (kind) {
      case "comment":
        return `${name} commented on your activity`;
      case "highfive":
        return `${name} gave you a high five!`;
      case "comment_star":
        return `${name} starred your comment`;
      case "mention":
        return `${name} mentioned you`;
      default:
        return null;
    }
  };

  const destinationFor = (n) => {
    if (n.activity_id) return `/activity/${n.activity_id}`;
    if (n.comment_id) return `/comment/${n.comment_id}`;
    const actor = n.actor || n.user || {};
    return actor?.id ? `/profile/${actor.id}` : undefined;
  };

  const fetchOtherNotifications = async () => {
    try {
      const res = await api.get("/notifications", {
        headers: authHeaders(),
        withCredentials: true,
      });
      const rows = Array.isArray(res.data) ? res.data : [];

      return rows
        .map((n, idx) => {
          const kind = mapType(n.type);
          if (!ALLOWED_KINDS.has(kind)) return null;

          if (CONTENT_KINDS.has(kind)) {
            const actor = n.actor || n.user || {};
            const name = normalizeName(actor);
            const avatar = actor?.avatar_url || actor?.profile_pic_url || null;

            const createdAt = safeIso(n.created_at);
            const keyBase =
              n.id ??
              `${kind}-${(actor.id ?? "0")}-${n.activity_id ?? n.comment_id ?? idx}-${createdAt || "na"}`;

            const msg = messageFor(kind, name);
            if (!msg) return null;

            return {
              key: `nt-${keyBase}`,
              kind,
              state: "info",
              name,
              avatar,
              userId: actor?.id ?? null,
              href: destinationFor(n),
              meta: { activity_id: n.activity_id, comment_id: n.comment_id, created_at: createdAt },
              message: msg,
            };
          }
          return null;
        })
        .filter(Boolean);
    } catch {
      return [];
    }
  };

  const fetchAll = async () => {
    try {
      const incoming = await fetchIncomingRequests();

      const rawOut = meId
        ? loadOutgoing().map((o) => ({
            ...o,
            key: `out-${o.id ?? o.connection_id ?? o.userId ?? `tmp-${Date.now()}`}`,
            kind: "outgoing",
            name: o.name || "User",
            avatar: o.avatar || null,
            state: o.state || "sent",
            userId: o.userId ?? null,
            href: o.userId ? `/profile/${o.userId}` : undefined,
            meta: { created_at: safeIso(o.created_at) },
          }))
        : [];

      const reconciledOut = await refreshOutgoingStatuses(rawOut);
      const others = await fetchOtherNotifications();

      const byKey = new Map();
      [...incoming, ...reconciledOut, ...others].forEach((m) => {
        if (!m) return;
        if (CONTENT_KINDS.has(m.kind) && !m.message) return;
        byKey.set(m.key, m);
      });

      setItems(sortByTimeThenName([...byKey.values()]));
    } catch (e) {
      console.error("Notifications fetch failed:", e?.response?.status, e?.response?.data || e.message);
    }
  };

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenProp, meId]);

  // ---- connection request actions (restored) ----
  async function accept(connectionId) {
    try {
      await api.post(
        `/connections/requests/${connectionId}/accept`,
        {},
        { headers: authHeaders(), withCredentials: true }
      );

      let requesterId = null;
      setItems((prev) => {
        let found = null;
        const next = prev.map((it) => {
          if (it.id === connectionId && it.kind === "incoming") {
            found = it;
            return { ...it, state: "accepted" };
          }
          return it;
        });
        requesterId = found?.userId ?? null;
        return sortByTimeThenName(next);
      });

      const st = await fetchConnStatus(requesterId);
      if (st === "accepted") {
        setItems((prev) =>
          sortByTimeThenName(
            prev.map((it) =>
              it.id === connectionId && it.kind === "incoming"
                ? { ...it, followStatus: "connected" }
                : it
            )
          )
        );
      }

      window.dispatchEvent(new CustomEvent("me:counts", { detail: { followersDelta: 1 } }));
    } catch (e) {
      console.error("Accept failed:", e?.response?.status, e?.response?.data || e.message);
    }
  }

  async function decline(connectionId) {
    try {
      await api.delete(`/connections/requests/${connectionId}`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setItems((prev) => sortByTimeThenName(prev.filter((it) => !(it.id === connectionId && it.kind === "incoming"))));
    } catch (e) {
      console.error("Decline failed:", e?.response?.status, e?.response?.data || e.message);
    }
  }

  async function followBack(userId, connectionId) {
    try {
      setItems((prev) =>
        sortByTimeThenName(prev.map((it) => (it.id === connectionId ? { ...it, followStatus: "pending" } : it)))
      );

      const res = await api.post(
        "/connections/request",
        { addressee_id: userId },
        { headers: authHeaders(), withCredentials: true }
      );

      const status = res?.data?.status;
      const followStatus =
        status === "accepted" ? "connected" :
        status === "pending_out" ? "sent" :
        "idle";

      if (followStatus === "connected") {
        window.dispatchEvent(new CustomEvent("me:counts", { detail: { followingDelta: 1 } }));
      }

      if (status === "pending_out") {
        window.dispatchEvent(
          new CustomEvent("connections:outgoing", {
            detail: {
              connection_id: res?.data?.connection_id,
              addressee: { id: userId, display_name: items.find((i) => i.id === connectionId)?.name },
              status: "pending_out",
            },
          })
        );
      }

      setItems((prev) =>
        sortByTimeThenName(prev.map((it) => (it.id === connectionId ? { ...it, followStatus } : it)))
      );
    } catch (e) {
      setItems((prev) =>
        sortByTimeThenName(prev.map((it) => (it.id === connectionId ? { ...it, followStatus: "idle" } : it)))
      );
    }
  }

  // Close popup and remove URL flag
  const closePopup = () => {
    if (open) {
      setOpen(false);
      removeNotifParam(location, navigate);
    }
  };

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) closePopup();
    };
    if (open) document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark all as seen when opened
  useEffect(() => {
    if (!open) return;
    const newSeen = new Set(seenKeys);
    items.forEach((it) => newSeen.add(it.key));
    setSeenKeys(newSeen);
    saveSeen(newSeen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If the URL has ?notifications=1, open the popup on load/navigation
  useEffect(() => {
    if (hasNotifParam(location)) {
      setOpen(true);
    }
  }, [location]);

  // Toggle popup (keep URL in sync)
  const toggleOpen = () => {
    setOpen((s) => {
      const next = !s;
      if (next) addNotifParam(location, navigate);
      else removeNotifParam(location, navigate);
      if (next) fetchAll();
      return next;
    });
  };

  // ---- badge count ----
  const unseenCount = items.reduce((acc, it) => acc + (seenKeys.has(it.key) ? 0 : 1), 0);
  const hasAlarm = unseenCount > 0;

  // Build returnTo for mobile/iOS deep views
  const shouldUseFullScreen = isMobile || isIOS();
  const addReturnTo = (path) => {
    const currentUrl = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    return `${path}${path.includes("?") ? "&" : "?"}returnTo=${encodeURIComponent(currentUrl)}`;
  };

  // Router-safe navigation; keep popup flag in URL so back reopens it
  const safeNavigate = (to) => {
    if (!to) return;
    const dest = shouldUseFullScreen ? addReturnTo(to) : to;
    if (open && !hasNotifParam(location)) addNotifParam(location, navigate);
    navigate(dest);
    setOpen(false);
  };

  const NameWithLink = ({ id, children }) =>
    id ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          safeNavigate(`/profile/${id}`);
        }}
        style={{ color: "inherit", textDecoration: "none", background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
      >
        {children}
      </button>
    ) : (
      <>{children}</>
    );

  const Avatar = ({ src, alt, id }) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (id) safeNavigate(`/profile/${id}`);
      }}
      style={{ background: "transparent", border: 0, padding: 0, cursor: id ? "pointer" : "default" }}
    >
      {src ? (
        <img src={src} alt={alt} style={{ width: 28, height: 28, borderRadius: "50%" }} />
      ) : (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "#f3f4f6",
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            color: "#6b7280",
            flex: "0 0 auto",
          }}
        >
          {String((alt || "").slice(0, 1) || "U").toUpperCase()}
        </div>
      )}
    </button>
  );

  // Click behavior per device
  const onRowClick = async (item) => {
    // Connection: go to profile
    if (CONNECTION_KINDS.has(item.kind)) {
      if (item.userId) safeNavigate(`/profile/${item.userId}`);
      return;
    }

    // Activity direct
    const activityId = item?.meta?.activity_id ?? item?.activity_id ?? null;
    if (activityId) {
      if (shouldUseFullScreen) {
        safeNavigate(`/activity/${activityId}`);
      } else {
        window.dispatchEvent(new CustomEvent("activity:open", { detail: { id: activityId } }));
        setOpen(false);
      }
      return;
    }

    // Comment -> resolve activity
    const commentId = item?.meta?.comment_id ?? item?.comment_id ?? null;
    if (commentId) {
      try {
        const res = await api.get(`/comments/${commentId}`, { headers: authHeaders(), withCredentials: true });
        const actId = res?.data?.activity_id ?? res?.data?.activity?.id ?? null;
        if (actId) {
          if (shouldUseFullScreen) {
            safeNavigate(`/activity/${actId}`);
          } else {
            window.dispatchEvent(new CustomEvent("activity:open", { detail: { id: actId } }));
            setOpen(false);
          }
          return;
        }
      } catch {
        /* fall through */
      }
    }

    // Fallback: use provided href (profile, etc.)
    if (item?.href) safeNavigate(item.href);
  };

  // ----- styles -----
  const containerStyleDesktop = {
    position: "absolute",
    right: 0,
    top: 36,
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    width: 440,
    boxShadow: "0 12px 24px rgba(0,0,0,0.08)",
    zIndex: 1000,
    overflow: "hidden",
    padding: "8px 0",
  };
  const containerStyleMobile = {
    position: "fixed",
    left: 12,
    right: 12,
    top: 64,
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    width: "auto",
    boxShadow: "0 16px 32px rgba(0,0,0,0.14)",
    zIndex: 1000,
    overflow: "hidden",
    padding: "8px 0",
  };
  const listStyle = isMobile
    ? {
        maxHeight: "70vh",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }
    : { maxHeight: 420, overflowY: "auto" };

  return (
    <div
      ref={ref}
      className={className}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        onClick={toggleOpen}
        aria-label="Notifications"
        style={{ cursor: "pointer", position: "relative", background: "transparent", border: 0, padding: 0 }}
      >
        <Sparkle size={28} weight={hasAlarm ? "duotone" : "fill"} className="text-blue-600" />
        {unseenCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              transform: (isMobile || isIOS())
                ? "translate(10%, -10%)"
                : "translate(90%, -40%)",
              background: BLUE_100,
              color: BLUE_600,
              borderRadius: 999,
              fontSize: 12,
              lineHeight: 1,
              padding: "3px 6px",
              minWidth: 18,
              textAlign: "center",
              fontWeight: 700,
            }}
          >
            {unseenCount}
          </span>
        )}
      </button>

      {open && (
        <div
          style={isMobile ? containerStyleMobile : containerStyleDesktop}
          role="dialog"
          aria-label="Notifications list"
        >
          <div style={listStyle}>
            {items.length ? (
              items.map((r) => {
                const isIncomingPending = r.kind === "incoming" && r.state === "request";
                const isIncomingAccepted = r.kind === "incoming" && r.state === "accepted";
                const isOutgoingPending = r.kind === "outgoing" && r.state === "sent";
                const isOutgoingAccepted = r.kind === "outgoing" && r.state === "accepted";
                const isConnection = CONNECTION_KINDS.has(r.kind);
                const infoText = !isConnection ? r.message : null;
                const clickable =
                  isConnection ||
                  !!infoText ||
                  !!r.meta?.activity_id ||
                  !!r.meta?.comment_id ||
                  !!r.href;

                return (
                  <div
                    key={r.key}
                    onClick={() => clickable && onRowClick(r)}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 14px",
                      gap: 12,
                      borderBottom: "1px solid #f8fafc",
                      cursor: clickable ? "pointer" : "default",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: "1 1 auto" }}>
                      <Avatar src={r.avatar} alt={r.name} id={r.userId} />
                      <div style={{ display: "flex", flexDirection: "column", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {isIncomingPending && (
                          <>
                            <span style={{ fontWeight: 600, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis" }}>
                              <NameWithLink id={r.userId}>{r.name}</NameWithLink>
                            </span>
                            <span style={{ fontSize: 12, color: "#6b7280" }}>sent you a request</span>
                          </>
                        )}

                        {isIncomingAccepted && (
                          r.followStatus === "connected" ? (
                            <span style={{ fontWeight: 600, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis" }}>
                              You and <NameWithLink id={r.userId}>{r.name}</NameWithLink> follow each other!
                            </span>
                          ) : (
                            <>
                              <span style={{ fontWeight: 600, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis" }}>
                                You are now connected with <NameWithLink id={r.userId}>{r.name}</NameWithLink>!
                              </span>
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                {r.followStatus === "sent"
                                  ? "Follow request sent"
                                  : r.followStatus === "pending"
                                  ? "Sending…"
                                  : "Follow back to connect both ways"}
                              </span>
                            </>
                          )
                        )}

                        {isOutgoingPending && (
                          <>
                            <span style={{ fontWeight: 600, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis" }}>
                              <NameWithLink id={r.userId}>{r.name}</NameWithLink>
                            </span>
                            <span style={{ fontSize: 12, color: "#6b7280" }}>Connection request sent.</span>
                          </>
                        )}

                        {isOutgoingAccepted && (
                          <>
                            <span style={{ fontWeight: 600, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis" }}>
                              You are now connected with <NameWithLink id={r.userId}>{r.name}</NameWithLink>!
                            </span>
                            <span style={{ fontSize: 12, color: "#6b7280" }}>(following)</span>
                          </>
                        )}

                        {infoText && (
                          <>
                            <span style={{ fontWeight: 600, fontSize: 16, overflow: "hidden", textOverflow: "ellipsis" }}>
                              <NameWithLink id={r.userId}>{r.name}</NameWithLink>
                            </span>
                            <span style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {infoText}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {isIncomingPending ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto", whiteSpace: "nowrap" }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); accept(r.id); }}
                          style={{
                            background: BLUE_600,
                            color: "white",
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: 8,
                            cursor: "pointer",
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          Accept
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); decline(r.id); }}
                          style={{
                            background: BLUE_300,
                            color: BLUE_700,
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: 8,
                            cursor: "pointer",
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          Decline
                        </button>
                      </div>
                    ) : isIncomingAccepted ? (
                      r.followStatus === "connected" ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }} />
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); followBack(r.userId, r.id); }}
                            disabled={r.followStatus === "pending" || r.followStatus === "connected"}
                            style={{
                              background: BLUE_600,
                              color: "white",
                              border: "none",
                              padding: "6px 10px",
                              borderRadius: 8,
                              cursor: r.followStatus === "connected" ? "default" : "pointer",
                              opacity: r.followStatus === "pending" ? 0.8 : 1,
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            {r.followStatus === "connected"
                              ? "Connected"
                              : r.followStatus === "sent"
                              ? "Request sent"
                              : r.followStatus === "pending"
                              ? "Sending…"
                              : "Follow back"}
                          </button>
                        </div>
                      )
                    ) : (
                      <div style={{ width: 16 }} />
                    )}
                  </div>
                );
              })
            ) : (
              <p style={{ padding: 14, textAlign: "center", color: "#6b7280" }}>No new notifications</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
